import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Trophy, Calendar, Users, Plus, ChevronRight, 
  MessageSquare, Bell, Loader2, Gamepad2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [feedRes, leaguesRes] = await Promise.all([
          api.getFeed(),
          api.getLeagues(),
        ]);
        setFeed(feedRes.data);
        setLeagues(leaguesRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Hoş Geldin, <span className="text-[#4DFFFF]">{user?.name?.split(" ")[0]}</span>
        </h1>
        <p className="text-[#B1C1C1] mt-1">Bugün ne oynayacaksın?</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Link to="/create-league" className="glass-card glass-card-hover p-4 flex items-center gap-3" data-testid="quick-create-league">
          <div className="w-10 h-10 rounded-lg bg-[#4DFFFF]/20 flex items-center justify-center">
            <Plus className="w-5 h-5 text-[#4DFFFF]" />
          </div>
          <div>
            <p className="font-['Barlow_Condensed'] uppercase tracking-wider text-white font-bold text-sm">Yeni Lig</p>
            <p className="text-xs text-[#64748B]">Oluştur</p>
          </div>
        </Link>
        
        <Link to="/games" className="glass-card glass-card-hover p-4 flex items-center gap-3" data-testid="quick-games">
          <div className="w-10 h-10 rounded-lg bg-[#C6A87C]/20 flex items-center justify-center">
            <Gamepad2 className="w-5 h-5 text-[#C6A87C]" />
          </div>
          <div>
            <p className="font-['Barlow_Condensed'] uppercase tracking-wider text-white font-bold text-sm">Oyunlar</p>
            <p className="text-xs text-[#64748B]">Keşfet</p>
          </div>
        </Link>
        
        <Link to="/messages" className="glass-card glass-card-hover p-4 flex items-center gap-3" data-testid="quick-messages">
          <div className="w-10 h-10 rounded-lg bg-[#F308C9]/20 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-[#F308C9]" />
          </div>
          <div>
            <p className="font-['Barlow_Condensed'] uppercase tracking-wider text-white font-bold text-sm">Mesajlar</p>
            <p className="text-xs text-[#64748B]">Görüntüle</p>
          </div>
        </Link>
        
        <Link to="/discover" className="glass-card glass-card-hover p-4 flex items-center gap-3" data-testid="quick-discover">
          <div className="w-10 h-10 rounded-lg bg-[#B1C1C1]/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#B1C1C1]" />
          </div>
          <div>
            <p className="font-['Barlow_Condensed'] uppercase tracking-wider text-white font-bold text-sm">Keşfet</p>
            <p className="text-xs text-[#64748B]">Kullanıcılar</p>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Feed */}
        <div className="lg:col-span-2">
          <div className="glass-card overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
              <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Aktivite Akışı
              </h2>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin mx-auto" />
              </div>
            ) : feed.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#B1C1C1]">Henüz aktivite yok</p>
                <p className="text-sm text-[#64748B] mt-1">Arkadaşlarını takip et veya lig oluştur</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {feed.map((activity) => (
                  <div key={activity.activity_id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4DFFFF]/20 to-[#4DFFFF]/5 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-[#4DFFFF]">
                          {activity.user?.name?.charAt(0) || "?"}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white">
                          <Link 
                            to={`/profile/${activity.user?.username}`}
                            className="font-semibold hover:text-[#4DFFFF]"
                          >
                            {activity.user?.name}
                          </Link>
                          {" "}
                          <span className="text-[#B1C1C1]">{activity.content}</span>
                        </p>
                        <p className="text-xs text-[#64748B] mt-1">{formatDate(activity.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* My Leagues */}
          <div className="glass-card overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                Liglerim
              </h3>
              <Link to={`/profile/${user?.username}`} className="text-xs text-[#4DFFFF] hover:underline">
                Tümü
              </Link>
            </div>
            
            {leagues.length === 0 ? (
              <div className="p-4 text-center">
                <Trophy className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                <p className="text-sm text-[#B1C1C1]">Henüz lig yok</p>
                <Link to="/create-league">
                  <Button size="sm" className="mt-2 bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                    <Plus className="w-3 h-3 mr-1" />
                    Oluştur
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {leagues.map((league) => (
                  <Link
                    key={league.league_id}
                    to={`/league/${league.league_id}`}
                    className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#C6A87C]/20 flex items-center justify-center">
                        <Trophy className="w-4 h-4 text-[#C6A87C]" />
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{league.name}</p>
                        <p className="text-xs text-[#64748B]">{league.game_name}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#64748B]" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Profile Card */}
          <div className="glass-card p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4DFFFF]/20 to-[#4DFFFF]/5 flex items-center justify-center border border-[#4DFFFF]/30">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-xl font-bold text-[#4DFFFF]">
                    {user?.name?.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <p className="font-semibold text-white">{user?.name}</p>
                <p className="text-sm text-[#64748B]">@{user?.username}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-white/5">
                <p className="text-lg font-bold text-[#4DFFFF]">{user?.leagues_count || 0}</p>
                <p className="text-xs text-[#64748B]">Lig</p>
              </div>
              <div className="p-2 rounded-lg bg-white/5">
                <p className="text-lg font-bold text-white">{user?.followers_count || 0}</p>
                <p className="text-xs text-[#64748B]">Takipçi</p>
              </div>
              <div className="p-2 rounded-lg bg-white/5">
                <p className="text-lg font-bold text-white">{user?.following_count || 0}</p>
                <p className="text-xs text-[#64748B]">Takip</p>
              </div>
            </div>
            <Link to={`/profile/${user?.username}`}>
              <Button variant="ghost" className="w-full mt-4 text-[#4DFFFF] hover:bg-[#4DFFFF]/10">
                Profili Görüntüle
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
